/**
 * Timezone utilities for detecting and managing user timezones
 */
import { getTimezoneOffset } from 'date-fns-tz'

/**
 * Get all available timezones from the browser's Intl API
 */
export function getAllTimezones(): string[] {
    try {
        return Intl.supportedValuesOf('timeZone')
    } catch (error) {
        console.warn('Failed to get all timezones:', error)
        // Fallback to a basic list if Intl.supportedValuesOf is not available
        return [
            'UTC',
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Paris',
            'Europe/Berlin',
            'Asia/Tokyo',
            'Asia/Shanghai',
            'Australia/Sydney'
        ]
    }
}

/**
 * Get timezone display name with proper formatting
 */
export function getTimezoneDisplayName(timezone: string): string {
    try {
        const now = new Date()
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'long'
        })
        const parts = formatter.formatToParts(now)
        const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value

        if (timeZoneName) {
            return `${timezone} (${timeZoneName})`
        }
        return timezone
    } catch (error) {
        console.warn('Failed to get timezone display name:', error)
        return timezone
    }
}

/**
 * Get common timezones using the library's standard list
 * Filters the complete timezone database to show the most commonly used ones
 */
export function getCommonTimezones() {
    const allTimezones = getAllTimezones()

    // Filter to commonly used timezones by region/city patterns
    const commonTimezones = allTimezones.filter(timezone => {
        // Include UTC
        if (timezone === 'UTC') return true

        // Include major cities and regions
        const commonPatterns = [
            // Pacific
            /^Pacific\/(Honolulu|Midway|Auckland|Fiji|Noumea|Port_Moresby|Tongatapu|Apia)$/,
            // America - Major cities
            /^America\/(New_York|Los_Angeles|Chicago|Denver|Phoenix|Anchorage|Toronto|Vancouver|Mexico_City|Sao_Paulo|Buenos_Aires|Santiago|Caracas|La_Paz|St_Johns)$/,
            // Europe - Major cities
            /^Europe\/(London|Paris|Berlin|Rome|Madrid|Amsterdam|Brussels|Vienna|Zurich|Stockholm|Oslo|Copenhagen|Warsaw|Prague|Budapest|Athens|Helsinki|Kiev|Bucharest|Sofia|Moscow|Istanbul|Dublin)$/,
            // Asia - Major cities
            /^Asia\/(Tokyo|Seoul|Shanghai|Hong_Kong|Taipei|Singapore|Manila|Jakarta|Bangkok|Kuala_Lumpur|Kolkata|Karachi|Dubai|Tehran|Baku|Yerevan|Tbilisi|Almaty|Tashkent|Vladivostok)$/,
            // Australia - Major cities
            /^Australia\/(Sydney|Melbourne|Brisbane|Perth|Adelaide)$/,
            // Africa - Major cities
            /^Africa\/(Cairo|Johannesburg|Casablanca|Accra)$/,
            // Atlantic
            /^Atlantic\/(Azores|Cape_Verde)$/
        ]

        return commonPatterns.some(pattern => pattern.test(timezone))
    })

    return commonTimezones.map(tz => ({
        value: tz,
        label: getTimezoneDisplayName(tz)
    }))
}

/**
 * Detect the user's timezone from the browser
 */
export function detectUserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        console.warn('Failed to detect timezone, falling back to UTC:', error)
        return 'UTC'
    }
}

/**
 * Get the current timezone offset in minutes (including DST)
 */
export function getCurrentTimezoneOffset(): number {
    return new Date().getTimezoneOffset()
}

/**
 * Get timezone offset in minutes for a specific timezone using date-fns-tz
 */
export function getTimezoneOffsetMinutes(timezone: string): number {
    try {
        if (timezone === 'UTC') {
            return 0 // UTC is always 0 offset
        }

        const now = new Date()
        // Use date-fns-tz library function directly
        const offsetMs = getTimezoneOffset(timezone, now)
        return Math.round(offsetMs / 60000) // Convert to minutes
    } catch (error) {
        console.warn('Failed to get timezone offset:', error)
        return 0
    }
}

/**
 * Get timezone offset for a specific date (to handle DST changes) using date-fns-tz
 */
export function getTimezoneOffsetForDate(timezone: string, date: Date): number {
    try {
        if (timezone === 'UTC') {
            return 0 // UTC is always 0 offset
        }

        // Use date-fns-tz library function directly
        const offsetMs = getTimezoneOffset(timezone, date)
        return Math.round(offsetMs / 60000) // Convert to minutes
    } catch (error) {
        console.warn('Failed to get timezone offset for date:', error)
        return 0
    }
}


/**
 * Format a date/time in a specific timezone
 */
export function formatDateTimeInTimezone(
    date: Date | string,
    timezone: string,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
    }
): string {
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date
        return new Intl.DateTimeFormat('en-US', {
            ...options,
            timeZone: timezone
        }).format(dateObj)
    } catch (error) {
        console.warn('Failed to format date in timezone:', error)
        return dateObj.toISOString()
    }
}

/**
 * Convert UTC date to user's timezone
 */
export function convertUTCToUserTimezone(utcDate: Date | string, userTimezone: string): Date {
    try {
        const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
        const utcTime = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000)
        const targetTime = new Date(utcTime + (getTimezoneOffset(userTimezone) * 60000))
        return targetTime
    } catch (error) {
        console.warn('Failed to convert UTC to user timezone:', error)
        return typeof utcDate === 'string' ? new Date(utcDate) : utcDate
    }
}

/**
 * Format timezone offset as UTC±HH:MM
 */
export function formatTimezoneOffset(offsetMinutes: number): string {
    const sign = offsetMinutes >= 0 ? '+' : '-'
    const absOffset = Math.abs(offsetMinutes)
    const hours = Math.floor(absOffset / 60)
    const minutes = absOffset % 60

    if (minutes === 0) {
        return `UTC${sign}${hours.toString().padStart(2, '0')}`
    } else {
        return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    }
}


/**
 * Validate if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
    try {
        Intl.DateTimeFormat(undefined, { timeZone: timezone })
        return true
    } catch (error) {
        return false
    }
}

